import type { IBatchStore } from './batch-store-types';
import logger from '../../utils/logger';
import { timerKey } from '../../utils/timer-manager/timer-manager';
import { DeviceStatus } from '../../constants/DeviceStatus';
import { toNumber } from '../../utils/numbers';
import { OUTPUT_TTL } from '../../constants/Pac';
import {
  MixerType,
  MixingStartPoint,
} from '../../constants/BatchTypeEnums';
import DischargeSequence from '../../containers/Discharge/DischargeSequence';
import {
  isReadyForFinalDischarge as _isReadyForFinalDischarge,
  mixerIsReadyForDischarge as _mixerIsReadyForDischarge,
  isMixerDischargeAllowed as _isMixerDischargeAllowed,
  isBootDeviceReadyForDischarge as _isBootDeviceReadyForDischarge,
  shouldWaitOnOtherScaleDischarge as _shouldWaitOnOtherScaleDischarge,
  showReadyForDischarge as _showReadyForDischarge,
} from './discharge-operations/discharge-readiness';
import { beltSwitch } from './device-switches/belt-switch';
import moment from 'moment';
import type { IBatchData } from '../../containers/Batch/IBatchData';
import type {
  IMixer,
  IPump,
  IScale,
  IVibrator,
} from '../../../types/device/DeviceModels';
import { IWeighedDataToBatch } from '../../../types/batch/DataToBatch';
import { aeratorSwitch } from './device-switches/aerator-switch';
import { screwSwitch } from './device-switches/screw-switch';
import { RemoteDisplayBatchStatus } from '../../constants/RemoteDisplayBatchStatus';
import { vibratorSwitch } from './device-switches/vibrator-switch';
import { BatchCycleStatus } from '../../constants/BatchCycleStatus';
import { reaction } from 'mobx';
import { VibratorMode } from '../../constants/DeviceModels';
import type {
  Discharge,
  DischargeScale,
} from '../../../types/api/Discharge';
import { MeteredMaterialType } from '../../constants/MaterialModels';
import type { Charge } from '../../../types/api/Charge';
import type { IMeteredMaterialModel } from '../../../types/material/MaterialModels';

export const dischargeSequenceMixin = {

  isTheSameDataInNextScale(this: IBatchStore, nextScale: IScale, initialData: IBatchData) {

    const nextScaleStatus = this.getDeviceStatus(nextScale.id);

    return initialData.uid === nextScaleStatus.uid && initialData.currentDrop === nextScaleStatus.currentDrop;
  },

  dischargeToNextScale(
    this: IBatchStore,
    data: IWeighedDataToBatch,
    nextScaleId: number,
    uid: string,
    initialData: IBatchData,
  ) {

    if (this.isAbort || !this.isActiveDrop(initialData)) {
      return;
    }

    const  { scale } = data;
    const nextScale = this.devicesStore.getDeviceById(nextScaleId);
    const weight = this.getScaleWeight(scale);

    this.timers.clear(timerKey.dischargeToNextScale(scale.id));

    if (!this.isNextScaleReadyForFeed(data, initialData)) {
      this.timers.set(timerKey.dischargeToNextScale(scale.id), () => {
        this.dischargeToNextScale(data, nextScale.id, uid, initialData);
      }, 2000);
      return;
    }

    if (!this.isScaleReadyForDischargeToNextScale(data, initialData)) {
      this.timers.set(timerKey.dischargeToNextScale(scale.id), () => {
        this.dischargeToNextScale(data, nextScale.id, uid, initialData);
      }, 1000);
      return;
    }

    this.isOverride = false;

    const scaleStatus = this.getDeviceStatus(scale.id);

    this.deviceStatus.set(scale.id, {
      ...scaleStatus,
      weight,
      status: DeviceStatus.DELAY,
    });

    aeratorSwitch(this.rootStore, scale.aeratorId);
    screwSwitch(this.rootStore, scale.screwId);

    const dischargeToNextScaleDelay = initialData.mixDesign && scale.dischargeAfterScaleId && scale.dischargeAfterScaleDelay
      ? scale.dischargeAfterScaleDelay * 1000
      : 1000;
    logger.log(`discharge ${scale.description} after scale delay ${nextScale.description}`, dischargeToNextScaleDelay);

    this.timers.set(timerKey.dischargeToNextScale(scale.id), () => {
      const scaleStatus = this.getDeviceStatus(scale.id);
      const weight = this.getScaleWeight(scale);
      const weightNext = this.getScaleWeight(nextScale);
      const nextScaleStatus = this.getDeviceStatus(nextScale.id);

      if (!this.isActiveDrop(initialData)) {
        return;
      }

      this.deviceStatus.set(scale.id, {
        ...scaleStatus,
        weight,
        status: DeviceStatus.DISCHARGING,
      });

      const nextScaleTarget = this.isTheSameDataInNextScale(nextScale, initialData)
        ? scaleStatus.target + nextScaleStatus.target
        : scaleStatus.target;
      logger.log('next scale target', nextScaleTarget, scale.description);

      this.deviceStatus.set(nextScale.id, {
        ...scaleStatus,
        id: nextScale.id,
        weight: weightNext,
        status: DeviceStatus.FEED,
        target: nextScaleTarget,
        error: false,
      });

      this.openScaleWithMode(scale);

      this.checkDischargeToScale(scale, nextScale, nextScaleTarget, initialData, () => {
        this.closeScale(scale);

        if (this.isDoubleGateScale(scale)) {
          this.closeScale(scale, scale.openGateTwoPNo, scale.closeGateTwoPNo);
        }

        if (scale.remoteDisplayId) {
          const { remoteDisplayStore } = this.rootStore;
          remoteDisplayStore.setStatus(scale.remoteDisplayId, {
            status: RemoteDisplayBatchStatus.FINISH,
            balance: '',
          });
        }

        this.deviceStatus.set(scale.id, this.getDeviceStatusDefault(scale));
        aeratorSwitch(this.rootStore, scale.aeratorId, false);
        screwSwitch(this.rootStore, scale.screwId, false);
        vibratorSwitch(this.rootStore, scale.vibratorId, false);

        if (this.isAbort || !this.isActiveDrop(initialData)) {
          return;
        }

        if (this.shouldWaitOnOtherScaleDischarge(nextScale)) {
          logger.log(`scale #${nextScale.id} ${nextScale.description} waiting on other scale to discharge`);
          return;
        }

        this.timers.clear(timerKey.dischargeToNextScaleDelay(nextScale.id));

        const dischargeOpenDelay = (nextScale.dischargeOpenDelay || 2) * 1000;
        logger.log(`discharge open delay ${scale.description} -> ${nextScale.description}`, dischargeOpenDelay);

        const nextScaleStatus = this.getDeviceStatus(nextScale.id);
        this.deviceStatus.set(nextScale.id, {
          ...nextScaleStatus,
          status: DeviceStatus.FEED,
          startTime: moment().add(nextScale.dischargeOpenDelay, 'seconds'),
        });

        this.timers.set(timerKey.dischargeToNextScaleDelay(nextScale.id), () => {

          this.nextStep();

          beltSwitch(this.rootStore, scale.beltId, false);

          const nextScaleStatus = this.getDeviceStatus(nextScale.id);
          this.deviceStatus.set(nextScale.id, {
            ...nextScaleStatus,
            status: DeviceStatus.WAIT_DISCH,
            startTime: null,
          });

          if (nextScale.parentScaleId) {
            this.dischargeToNextScale(Object.assign(data, { scale: nextScale }), nextScale.parentScaleId, uid, initialData);
          } else {
            const runningData = Object.assign({}, {
              scaleId: nextScale.id,
              uid: initialData.uid,
              tId: initialData.tId,
              currentDrop: initialData.currentDrop,
            });
            this.waitingForDischarge.push(runningData);

            if (!this.isDischarge && this.isReadyForFinalDischarge(initialData)) {
              this.showReadyForDischarge(initialData);
              this.context.onBatchCycle(BatchCycleStatus.LOAD_READY_FOR_DISCHARGE, this.inputs, this.outputs, this.channels);
            }

            if (this.isDischarge && !this.discharging) {
              this.timers.set(timerKey.dischargeToNextScaleTimer(), () => {
                if (this.isDischarge && !this.discharging) {
                  this.discharge();
                }
              }, 1000);
            }
          }

        }, dischargeOpenDelay);

      });

    }, dischargeToNextScaleDelay);
  },

  showReadyForDischarge(this: IBatchStore, initialData: IBatchData) {

    return _showReadyForDischarge(this, initialData);
  },

  shouldWaitOnOtherScaleDischarge(this: IBatchStore, scale: IScale) {
    return _shouldWaitOnOtherScaleDischarge(this, scale);
  },

  checkDischargeToScale(this: IBatchStore, scale: IScale, nextScale: IScale, target: number, initialData: IBatchData, onFinish: () => void = () => {}) {

    let vibrator: IVibrator, vibrationOn, dischargeTare;
    let flowRateProbes: number[] = [];
    let weight = this.getScaleWeight(scale);
    let newWeight = 0;

    if (scale.vibratorId) {
      vibrator = this.devicesStore.getDeviceById(scale.vibratorId);
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(initialData)) {
          reaction.dispose();
          return;
        }

        if (this.isEmergencyStop || initialData.isHoldLoad) {
          this.holdScale(scale);

          if (scale.beltId && this.isBeltOn(scale.beltId)) {
            beltSwitch(this.rootStore, scale.beltId, false);
          }

          return;
        }

        scale = { ...scale, ...this.devicesStore.getDeviceById(scale.id) };
        newWeight = this.getScaleWeight(scale);

        if (scale.remoteDisplayId) {
          const { remoteDisplayStore } = this.rootStore;
          remoteDisplayStore.setStatus(scale.remoteDisplayId, {
            status: RemoteDisplayBatchStatus.DISCHARGE,
            currentWeight: `${newWeight}`,
          });
        }

        if (vibrator) {
          vibrationOn = scale.vibrationOn || 500;
          dischargeTare = scale.dischargeTare || 0;
          const minimumDischargeRate = scale.minimumDischargeRate || 0;
          const flowRate = weight - newWeight;
          const hysteresis = Math.max(scale.hysteresis, 0);
          const flowRateProbesSize = Math.max(Math.round((hysteresis * 10) / 2), 5);

          if (flowRateProbes.length < flowRateProbesSize) {
            flowRateProbes.push(flowRate);
          }

          if (vibrator.mode !== VibratorMode.MinFlow) {
            if (newWeight <= vibrationOn && newWeight > dischargeTare) {
              vibratorSwitch(this.rootStore, scale.vibratorId);
            } else if (newWeight <= dischargeTare) {
              vibratorSwitch(this.rootStore, scale.vibratorId, false);
            }
          }

          if (flowRateProbes.length === flowRateProbesSize && !initialData.isWashout) {
            const averageFlowRate = Math.round(flowRateProbes.reduce((a, b) => a + b, 0) / (flowRateProbes.length/5));
            logger.log('flow rate', scale.description, averageFlowRate, '<->', minimumDischargeRate, flowRateProbes,
              vibrator.description, vibrator.mode);

            if (vibrator.mode === VibratorMode.MinFlow) {
              if (newWeight <= dischargeTare) {
                vibratorSwitch(this.rootStore, scale.vibratorId, false);
              } else if (averageFlowRate < minimumDischargeRate) {
                vibratorSwitch(this.rootStore, scale.vibratorId);
              } else {
                vibratorSwitch(this.rootStore, scale.vibratorId, false);
              }
            }

            flowRateProbes = [];
          }

          weight = newWeight;
        }

        const nextScaleWeight = nextScale ? this.getScaleWeight(nextScale) : 0;
        const dischargeTargetTolerance = nextScale ? (nextScale.metricDischargeTare || 10) : 10;

        if (this.isScaleFinishedDischargeEarlier(scale.id)
          || (newWeight <= (scale.dischargeTare || 0) && nextScaleWeight + dischargeTargetTolerance >= target)) {
          reaction.dispose();
          onFinish();
        }
      },
    );
  },

  checkDecumDischargeToNextScale(this: IBatchStore, nextScale: IScale, target: number, initialData: IBatchData, onFinish: () => void = () => {}) {

    const weight = nextScale ? this.getScaleWeight(nextScale) : 0;
    const dischargeTargetTolerance = nextScale ? (nextScale.metricDischargeTare || 10) : 10;

    if (weight + dischargeTargetTolerance >= target) {
      onFinish();
      return;
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(initialData)) {
          reaction.dispose();
          return;
        }

        if (this.isEmergencyStop) {
          return;
        }

        const weight = nextScale ? this.getScaleWeight(nextScale) : 0;

        if (weight + dischargeTargetTolerance >= target) {
          reaction.dispose();
          onFinish();
        }
      },
    );
  },

  isReadyForFinalDischarge(this: IBatchStore, runningData: IBatchData) {

    return _isReadyForFinalDischarge(this, runningData);
  },

  getSequence(this: IBatchStore, batchData: IBatchData) {
    let defaultFlowRate;
    let sequence;
    let charge;
    let discharges: DischargeScale[] = [];

    const ds = this.dischargeStore;

    if (!batchData?.dischargeSequenceId || !Number(batchData?.dischargeSequenceId)) {
      sequence = ds.getDefaultSequence();
      logger.log('sequence - default', sequence);
    } else {
      sequence = batchData.sequence || ds.sequences.find((v: Discharge) => v.id === Number(batchData.dischargeSequenceId));
      logger.log('sequence - edit screen', batchData.dischargeSequenceId);
    }

    if (batchData.mixId) {
      charge = ds.mixCharge.find((v: Charge) => v.mixItemId === Number(batchData.mixId) && v.dischargeSequenceId === Number(batchData.dischargeSequenceId));
      logger.log('sequence - mix charge', batchData.mixId, charge);
    }

    if (!charge && batchData.truckId) {
      charge = ds.truckCharge.find((v: Charge) => v.truckId === Number(batchData.truckId) && v.dischargeSequenceId === Number(batchData.dischargeSequenceId));
      logger.log('sequence - truck charge', batchData.truckId, charge);
    }

    if (!charge && ds.loadSizeCharge.length) {
      charge = ds.loadSizeCharge.find((v: Charge) => batchData.dropSize >= v.loadSize && v.dischargeSequenceId === Number(batchData.dischargeSequenceId));
      logger.log('sequence - load size charge', charge);
    }

    sequence?.discharges?.forEach((d: DischargeScale) => {
      const scale = this.devicesStore.getDeviceById(d.scaleId);
      let target = scale ? this.getScaleWeight(scale) : 0;
      let scaleName = scale ? scale.description : '';
      if (scale?.decumulative) {
        target = this.getDecumTarget(scale.id, batchData);
      }
      const type = ds.getTypeForScale(d.scaleId);
      const unit = ds.getUnitForScale(d.scaleId);

      discharges.push({
        ...d,
        target,
        type,
        unit,
        scaleName,
      });
    });

    defaultFlowRate = charge?.rate ? charge.rate : sequence.defaultFlowRate;

    if (batchData.mixerId) {
      const mixer = this.devicesStore.getDeviceById(Number(batchData.mixerId));
      if (mixer?.chargingRate) {
        defaultFlowRate = mixer.chargingRate;
      }
    }

    const speeds = ds.calculateFlowRate(discharges, defaultFlowRate, sequence.useDiscreteDischarge);

    discharges.forEach((v: DischargeScale, i: number) => {
      v.flowRate = Math.min(speeds[i], defaultFlowRate);
    });

    logger.log('sequence discharges:', discharges, 'defaultFlowRate: ', defaultFlowRate);

    return new DischargeSequence({
      ...sequence,
      defaultFlowRate,
      discharges,
    });
  },

  toggleMixer(this: IBatchStore, mixerId: number, isActive: boolean) {

    let mixer = this.devicesStore.getDeviceById(mixerId);

    if (mixer) {
      mixer = { ...mixer, isActive: typeof isActive === 'undefined' ? !mixer.isActive : isActive };
      this.devicesStore.updateDevice(mixer.id, mixer.deviceTypeName, mixer);
    }
  },

  isBootDeviceReadyForDischarge(this: IBatchStore) {

    return _isBootDeviceReadyForDischarge(this);
  },

  isMixerOn(this: IBatchStore, mixer: IMixer) {

    if (mixer.mixerOffPNo && mixer.type === MixerType.Central) {
      const mixerStatus = this.getDeviceStatus(mixer.id);
      return this.isOutputOn(mixer.mixerOnPNo) || mixerStatus.active;
    }

    return this.isOutputOn(mixer.mixerOnPNo);
  },

  mixerIsReadyForDischarge(this: IBatchStore, runningData = {}) {
    return _mixerIsReadyForDischarge(this, runningData);
  },

  getTemperWater(this: IBatchStore, mixer: IMixer) {

    if (!mixer?.tWaterMeterStatusPNo) {
      return 0;
    }

    return toNumber(this.meters[mixer.tWaterMeterStatusPNo - 1]);
  },

  getTemperWaterData(this: IBatchStore, mixer: IMixer) {

    if (!mixer?.tWaterMeterStatusPNo) {
      return {
        meterRate: 1,
      };
    }

    const pump = this.devicesStore.pumps.find((v: IPump) => v.meterPNo === mixer.tWaterMeterStatusPNo);
    if (pump) {
      const material = this.materialStore.materialsWithDetails.find((v: IMeteredMaterialModel) => v.pumpId === pump.id);
      if (material) {
        return {
          unitSymbol: material.itemBatchUnitSymbol,
          unitId: material.itemBatchUnitId,
          meterRate: material.meterRate || 1,
          meterPNo: pump.meterPNo,
        };
      }
    }

    return {
      meterRate: 1,
    };
  },

  checkBatchProgress(this: IBatchStore, data: IBatchData, onFinish: (data: IBatchData) => void = () => {}) {

    // @ts-ignore
    this.clearTimeout(this[`batchProgressTimer-${data.tId}`]);

    // @ts-ignore
    this[`batchProgressTimer-${data.tId}`] = this.setTimeout(() => {

      if (!this.isActiveDrop(data) || !data.mixDesign) {
        return;
      }

      let all = 0;
      let done = 0;

      if (data.pumps) {
        all += Object.values(data.pumps).length;
        done += Object.values(data.pumps).filter((v: any) =>
          (v.isDischarged && v.materialDetails.type === MeteredMaterialType.Admixture)
          ||
          (v.materialDetails.type !== MeteredMaterialType.Admixture
            && (v.headWaterDischarged || !v.headWater) && (v.washWaterDischarged || !v.washWater)
          ),
        ).length;
      }

      if (data.scales) {
        Object.values(data.scales).forEach((x: any) => {
          all += x.length;
          done += x.filter((v: IWeighedDataToBatch) => v.isFeedFinish).length;
        });
      }

      if (all === done) {
        logger.log('weighed done:', data.tId, all, done);
        onFinish(data);
        return;
      }

      logger.log('weighed in progress:', data.tId, all, done);

      this.checkBatchProgress(data, onFinish);

    }, 1000);
  },

  printAsSoonAsMaterialsAreWeighed(this: IBatchStore, runningData: IBatchData) {
    const { settingsStore, ticketStore } = this.rootStore;
    const { configuration } = settingsStore;

    if (configuration?.printAsSoonAsMaterialsAreWeighed) {
      const data = this.running.find(v => v.uid === runningData.uid && v.currentDrop === runningData.currentDrop);
      const dataIndex = this.running.findIndex(v => v.uid === runningData.uid && v.currentDrop === runningData.currentDrop);
      const nextData = this.running.length > dataIndex + 1 ? this.running[dataIndex + 1] : this.queue.length ? this.queue[0] : null;

      if (nextData && nextData.uid === data.uid) {
        logger.log(`${data.uid} drop ${data.currentDrop} of ${data.allDrops} - next drop`);
        return;
      }

      const prevData = this.running.find(v => v.uid === data.uid && v.currentDrop === data.currentDrop-1);
      if (prevData) {
        //fixing issue when previous drop still running, need to calculate prev drops
        const ticket = ticketStore.calculateTicketData(prevData);
        data.results = ticket.results;
      }

      if (!data.resultsSending) {
        const loadEndTime = moment().tz(this.timezone).add((data.loadStartTimeDiff || 0) * -1).format();
        this.saveDropTimes(data, {
          dropNumber: data.currentDrop,
          loadEndTime,
        });
        const ticket = ticketStore.calculateTicketData({ ...data, loadEndTime });
        data.loadEndTime = loadEndTime;
        data.results = ticket.results;
        data.drops = ticketStore.convertDrops(data.batchDrops);
        logger.log('print ticket as soon as materials are weighed', ticket.id);

        if (data.batchStartedOffline) {
          this.printTicketOnBatchEnd(data, ticket);
        } else {
          data.resultsSending = true;
          logger.log(`finish and print: ${data.uid} drop ${data.currentDrop} of ${data.allDrops} - batch finish `, data);
          this.saveLog(`Sending batch weight results, as soon as materials are weighed ${data.id}`);

          ticketStore.updateTicketOnBatchEnd(data)
            .then((resp: any) => {
              logger.log('Batch ticket end', resp);
              this.saveLog(`Batch weights results sent, as soon as materials are weighed ${data.id}`);
              const currentData = this.running.find(v => v.uid === data.uid && v.currentDrop === data.currentDrop) || {};
              currentData.resultsSent = true;
              const ticket = ticketStore.calculateTicketData(resp);
              this.printTicketOnBatchEnd(data, ticket);
            })
            .catch((e: Error) => {
              this.showError({ key: 'Couldn\'t update ticket on batch end', props: { error: e.message } });
              this.printTicketOnBatchEnd(data, ticket);
              if (!data.batchStartedOffline) {
                ticketStore.updateTicketOnBatchEndOffline({
                  ...ticket,
                  batchFinishOffline: true,
                });
              }
            });
        }
      }
    }
  },

  dischargeMixer(this: IBatchStore, runningData: IBatchData) {

    const mixer = this.devicesStore.getDeviceById(Number(runningData.mixerId));

    if (!mixer || runningData.isWashout) {
      this.finishDischarge(runningData);
      return;
    }

    this.discharging = false;

    if (
      (this.running.length === 1 && !this.queue.length)
      ||
      (this.running.length > 1 && this.running[1] && this.running[0].uid !== this.running[1].uid)
      ||
      (this.running.length === 1 && this.queue[0] && this.running[0].uid !== this.queue[0].uid)
    ) {
      this.isDischarge = false;
      this.isHoldDischarge = false;
    }

    const { uid, tId, truckCode, currentDrop, allDrops, mix } = runningData;
    runningData.mixingStartTime = Date.now();
    let mixingTime = mixer.mixingTime;

    if (mix?.mixingTime) {
      mixingTime = mix.mixingTime;
      logger.log('mixing time from mix:', mix.itemCode, mixingTime);
    }

    this.deviceStatus.set(mixer.id, {
      ...this.getDeviceStatus(mixer.id),
      id: mixer.id,
      status: DeviceStatus.MIXING,
      delay: mixingTime,
      restart: mixer.mixingStartPoint === MixingStartPoint.OnAllMaterialsLoaded,
      uid,
      currentDrop,
      allDrops,
      tId,
      truckCode,
    });

    this.onDeviceStatus(mixer, [DeviceStatus.MIXING], () => {
      this.startBatch();
    });

    if (mixer.dischreadyLightPNo) {
      const dischargeReadyTimer = this.setTimeout(() => {
        if (this.getDeviceStatus(mixer.id).status === DeviceStatus.MIXING) {
          this.setOutput(mixer.dischreadyLightPNo, OUTPUT_TTL);
        }
      }, mixingTime * 1000 || 0);

      this.onDeviceStatus(mixer, [DeviceStatus.TOP_OPEN], () => {
        this.clearTimeout(dischargeReadyTimer);
        this.setOutput(mixer.dischreadyLightPNo, 0);
      });
    }

    this.onDeviceStatus(mixer, [DeviceStatus.END_MIXING, DeviceStatus.TOP_OPEN], () => {
      if (runningData.mixingTime) {
        return;
      }

      const startTime =
        mixer.mixingStartPoint === MixingStartPoint.OnFirstMaterialLoaded
          ? (runningData.mixerFeedingStartTime ?? runningData.mixingStartTime)
          : runningData.mixingStartTime;

      runningData.mixingTime = toNumber((Date.now() - (startTime ?? Date.now())) / 1000);
      logger.log('mixing time:', runningData.mixingTime, mixer.mixingStartPoint);
    });

    if (mixer.type !== MixerType.ForwardReverse && mixer.automaticMode) { // not for F-R mixer
      this.onDeviceStatus(mixer, [DeviceStatus.RETURNING], () => {
        this.setOutput(mixer.closeReturnPNo, OUTPUT_TTL);

        this.checkMixerFinishReturning(mixer, runningData, () => {
          this.setOutput(mixer.closeReturnPNo, 0);
        });
      });
    }

    if (mixer.type === MixerType.ForwardReverse) {

      this.onDeviceStatus(mixer, [ DeviceStatus.SWITCHING ], () => {
        this.setOutput(mixer.openTiltPNo, 0);
      });

      this.onDeviceStatus(mixer, [ DeviceStatus.CLOSE_DELAY ], () => {
        this.setOutput(mixer.closeReturnPNo, OUTPUT_TTL);
      });
    }

    this.onDeviceStatus(mixer, [ DeviceStatus.FINISH ], () => {

      const temperWaterData = this.getTemperWaterData(mixer);
      runningData.temperWaterFinish = this.getTemperWater(mixer) * temperWaterData.meterRate;
      runningData.temperWater = runningData.temperWaterFinish - runningData.temperWaterStart;
      logger.log('finish temper water', runningData.temperWaterFinish, runningData.temperWaterUnitSymbol);
      logger.log('temper water:', runningData.temperWater, runningData.temperWaterUnitSymbol);

      this.timers.set(timerKey.mixerFinishDelay(mixer.id), () => {
        const s = this.getDeviceStatus(mixer.id);

        if (s.status === DeviceStatus.FINISH) {
          this.deviceStatus.set(mixer.id, {
            ...this.getDeviceStatusDefault(mixer),
            active: s.active,
            id: mixer.id,
          });
        }

        this.finishDischarge(runningData);

        if (this.isDischarge) {
          this.discharge();
        }

      }, 1000);

    });
  },

  checkMixerFinishReturning(this: IBatchStore, mixer: IMixer, initialData: IBatchData, onFinish: () => void = () => {}) {

    if (!this.isInputPortOff(mixer.closeReturnStatusPNo)) {
      onFinish();
      return;
    }

    reaction(
      () => this.statusCount,
      (counter, reaction) => {
        if (this.isAbort || !this.isActiveDrop(initialData)) {
          reaction.dispose();
        }

        if (!this.isInputPortOff(mixer.closeReturnStatusPNo)) {
          onFinish();
          reaction.dispose();
        }
      },
    );
  },

  checkBeltsRunning(this: IBatchStore, runningData: IBatchData, onFinish: () => void = () => {}) {

    let isBeltOn = false;

    logger.log('check belts are running', runningData.uid, runningData.currentDrop);

    if (!this.devicesStore.belts.length || runningData.isWashout) {
      onFinish();
      return;
    }

    if (runningData.finalScales && Object.keys(runningData.finalScales).length) {
      Object.keys(runningData.finalScales).forEach(scaleId => {
        const scale = runningData.finalScales[scaleId];
        const sd = this.getDeviceStatus(scale.id);

        if (scale.beltId && this.isBeltOn(scale.beltId) && sd.uid === runningData.uid && sd.currentDrop === runningData.currentDrop) {
          isBeltOn = true;
        }
      });
    }

    this.timers.clear(timerKey.beltsRunning());

    if (isBeltOn) {
      this.timers.set(timerKey.beltsRunning(), () => {
        this.checkBeltsRunning(runningData, onFinish);
      }, 1000);
    } else {
      onFinish();
    }
  },

  isMixerDischargeAllowed(this: IBatchStore, mixer: IMixer) {
    return _isMixerDischargeAllowed(this, mixer);
  },

  isMixerFinishAllowed(this: IBatchStore, mixer: IMixer) {

    if (!mixer?.analogInputId) {
      return true;
    }

    const analogInput = this.rootStore.devicesStore.analogInputs.find(v => v.id === mixer.analogInputId);

    if (!analogInput) {
      return true;
    }

    const value = this.getAnalogInputValue(analogInput.id);
    const isAllowed = !mixer.allowFinishAt || value <= mixer.allowFinishAt;

    logger.log('is mixer finish allowed:', isAllowed, mixer.allowFinishAt, value);

    if (!isAllowed) {
      this.showWarning({ key: 'Mixer is waiting on Analog Input',
        props: {
          mixerId: mixer.id,
          mixerName: mixer.description || '',
          allowFinishAt: mixer.allowFinishAt || 0,
        }});
    }

    return isAllowed;
  },

  rampUpMixer(this: IBatchStore, mixerId: number, up: boolean = true, index: number = -1) {

    const mixerWithAnalogSwitch = this.devicesStore.mixers.find((m: IMixer) => m.analogSwitchId);

    if (!mixerWithAnalogSwitch) {
      return;
    }

    const analogSwitch = this.devicesStore.getDeviceById(mixerWithAnalogSwitch.analogSwitchId);

    this.timers.clear(timerKey.rampUp());

    if (analogSwitch && Array.isArray(analogSwitch.rampUps) && analogSwitch.rampUps.length) {
      if (up && index < analogSwitch.rampUps.length) {
        index = index === -1 ? 0 : index;
        this.setAnalog(analogSwitch.channelPNo, analogSwitch.rampUps[index].value);
        logger.log('analog switch ramp up', up, index, analogSwitch.rampUps[index].time, analogSwitch.rampUps[index].value);

        this.timers.set(timerKey.rampUp(), () => {
          this.rampUpMixer(mixerId, up, index + 1);
        }, analogSwitch.rampUps[index].time * 1000);

      } else if (!up) {
        index = index === -1 ? analogSwitch.rampUps.length - 1 : index;
        this.setAnalog(analogSwitch.channelPNo, analogSwitch.rampUps[index].value);
        logger.log('analog switch ramp up', up, index, analogSwitch.rampUps[index].time, analogSwitch.rampUps[index].value);

        if (index > 0) {
          this.timers.set(timerKey.rampUp(), () => {
            this.rampUpMixer(mixerId, up, index - 1);
          }, analogSwitch.rampUps[index].time * 1000);
        }
      }
    }
  },

};
